/**
 * The list's "Resources Enabled" cell: running channels as compact chips, with a
 * direction glyph when the direction is known, collapsing the rest into "+N more".
 */
import { Box, Stack, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { EhrSyncResource } from '@models/ehr-sync';

import { directionMessages } from '../../messages';
import {
  DIRECTION_GLYPH,
  summarizeRunningResources,
} from '../shared/monitoring.util';

const messages = defineMessages({
  noneRunning: {
    id: 'ehrSync.list.resources.noneRunning',
    defaultMessage: 'None running',
  },
  more: {
    id: 'ehrSync.list.resources.more',
    defaultMessage: '+{count} more',
  },
});

interface ResourcesEnabledCellProps {
  resources: EhrSyncResource[];
}

const chipSx = {
  color: 'text.secondary',
  bgcolor: 'action.hover',
  borderRadius: 0.75,
  px: 0.875,
  py: 0.375,
  whiteSpace: 'nowrap',
} as const;

export default function ResourcesEnabledCell({
  resources,
}: ResourcesEnabledCellProps) {
  const { formatMessage } = useIntl();
  const { shown, overflow } = summarizeRunningResources(resources);

  if (!shown.length) {
    return (
      <Typography
        component="span"
        variant="caption"
        fontWeight="bold"
        sx={chipSx}
      >
        {formatMessage(messages.noneRunning)}
      </Typography>
    );
  }

  return (
    <Stack direction="row" flexWrap="wrap" spacing={0.5} useFlexGap>
      {shown.map((resource, index) => (
        <Typography
          key={`${resource.resourceType}-${index}`}
          component="span"
          variant="caption"
          fontWeight="bold"
          sx={chipSx}
        >
          {resource.resourceType}
          {resource.direction ? (
            <>
              {' '}
              <span aria-hidden>{DIRECTION_GLYPH[resource.direction]}</span>
              <Box component="span" sx={visuallyHidden}>
                {formatMessage(directionMessages[resource.direction])}
              </Box>
            </>
          ) : null}
        </Typography>
      ))}
      {overflow > 0 ? (
        <Typography
          component="span"
          variant="caption"
          fontWeight="bold"
          sx={chipSx}
        >
          {formatMessage(messages.more, { count: overflow })}
        </Typography>
      ) : null}
    </Stack>
  );
}
